import React, { useState } from 'react';
import { Variant } from '@services';
import { formatPrice, pushAddToCartEvent } from '@utils';
import { Divider } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { AddToCartButton } from '@components';

import styles from './MembershipCard.module.scss';
import classnames from 'classnames/bind';
import { MembershipCardSkeleton } from './MembershipCard.skeleton';

const cx = classnames.bind(styles);
const CN = 'membership-card';
const nameClassName = `${CN}__name`;
const nameVipClassName = `${nameClassName}--vip`;
const priceClassName = `${CN}__price`;
const featureClassName = `${CN}__feature`;
const includedFeatureClassName = `${featureClassName}--included`;
const notIncludedFeatureClassName = `${featureClassName}--not-included`;
const buttonClassName = `${CN}__button`;
const buttonVipClassName = `${buttonClassName}--vip`;
const checkIconClassName = `${CN}__icon--check`;
const closeIconClassName = `${CN}__icon--close`;
const featuresClassName = `${CN}__features`;

interface Feature {
    name: string;
    included: boolean;
}

interface MembershipCardProps {
  variant: Variant;
  onAddToCart?: (variantId: number) => Promise<void>;
  showButton: boolean;
  features: Feature[];
  isLoading?: boolean;
}

export const MembershipCard:React.FC<MembershipCardProps> = ({
  variant,
  features,
  showButton = true,
  onAddToCart,
  isLoading
}) => {
  const isVip = features.every(({ included }) => included);
  const [ isAddingToCart, setIsAddingToCart ] = useState(false);

  const onClick = async (variantId: number): Promise<void> => {
    setIsAddingToCart(true);
    if (onAddToCart && variantId) {
      await onAddToCart(variantId);
    }
    pushAddToCartEvent(variant, 1);
    setIsAddingToCart(false);
  };

  if (isLoading) {
    return <MembershipCardSkeleton />;
  }

  return <div className={ cx(CN) }>
    <div className={ cx(nameClassName, isVip && nameVipClassName) }>{ variant.attributes.name }</div>
    <div className={ cx(priceClassName) }>{ formatPrice(variant.price) }</div>
    <Divider/>
    <div className={ cx(featuresClassName) }>
      { features.map(({ name, included }) => <div key={ name } className={ cx(featureClassName) }>
        { included ?
          <CheckIcon fontSize='small' className={ cx(checkIconClassName) }/> :
          <CloseIcon fontSize='small' className={ cx(closeIconClassName) }/>
        }
        <span className={ cx(included ? includedFeatureClassName : notIncludedFeatureClassName) }>{ name }</span>
      </div>)}
    </div>
    { showButton && <AddToCartButton
      className={ cx(buttonClassName, isVip && buttonVipClassName) }
      isLoading={ isAddingToCart }
      buttonText={ `Choose ${variant.attributes.name}` }
      onClick={ (): Promise<void> => onClick(variant.id as number) }
      isOnStock={ variant.isOnStock }
    /> }
  </div>;
};
